// ADE Game FX — suoni WebAudio (zero asset) + vibrazione/haptics
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

let ctx;
const ac = () => {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  } catch { return null; }
};

const tone = (freq, t0, dur, type = "sine", vol = 0.16, slide = 0) => {
  const c = ac();
  if (!c) return;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, c.currentTime + t0);
  if (slide) o.frequency.exponentialRampToValueAtTime(slide, c.currentTime + t0 + dur);
  g.gain.setValueAtTime(0.0001, c.currentTime + t0);
  g.gain.exponentialRampToValueAtTime(vol, c.currentTime + t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(c.currentTime + t0);
  o.stop(c.currentTime + t0 + dur + 0.05);
};

const haptic = async (kind) => {
  try {
    if (kind === "light") await Haptics.impact({ style: ImpactStyle.Light });
    else if (kind === "medium") await Haptics.impact({ style: ImpactStyle.Medium });
    else if (kind === "success") await Haptics.notification({ type: NotificationType.Success });
  } catch {
    try { navigator.vibrate?.(kind === "success" ? [40, 60, 80] : kind === "medium" ? 30 : 15); } catch {}
  }
};

// Like: pop morbido + tap leggero
export const fxLike = () => {
  tone(660, 0, 0.09, "sine", 0.12, 990);
  haptic("light");
};

// Mission completata (dwell raggiunto): campana di conferma + tap medio
export const fxMissionComplete = () => {
  tone(523, 0, 0.14, "triangle", 0.15);
  tone(784, 0.12, 0.22, "triangle", 0.15);
  haptic("medium");
};

// Reward: cascata di monete (arpeggio brillante) + haptic success
export const fxReward = () => {
  [880, 1109, 1319, 1760].forEach((f, i) => tone(f, i * 0.11, 0.18, "triangle", 0.14));
  tone(2637, 0.46, 0.3, "sine", 0.09);
  haptic("success");
  setTimeout(() => haptic("light"), 260);
  setTimeout(() => haptic("light"), 520);
};

// Reazione storia a pieno schermo: impatto cinematico (sub-boom + riser + scintille) + haptics forte
export const fxStoryReaction = (isAde = false) => {
  tone(190, 0, 0.55, "sine", 0.24, 44);
  tone(96, 0.02, 0.66, "triangle", 0.17, 38);
  [1320, 1760, 2200, 2637].forEach((f, i) => tone(f, 0.05 + i * 0.045, 0.15, "triangle", 0.075));
  if (isAde) {
    tone(523, 0.3, 0.2, "triangle", 0.12);
    tone(1047, 0.44, 0.34, "sine", 0.1);
  }
  haptic("success");
  setTimeout(() => haptic("medium"), 90);
  setTimeout(() => haptic("light"), 210);
};

// Messaggio inviato: pop soft ascendente + tap leggero
export const fxMessageSent = () => {
  tone(520, 0, 0.07, "sine", 0.08, 760);
  haptic("light");
};

// Messaggio ricevuto: doppio tono soft discendente
export const fxMessageReceived = () => {
  tone(760, 0, 0.09, "sine", 0.09);
  tone(560, 0.09, 0.13, "sine", 0.08);
  haptic("light");
};

// Uncover Gift: suoni per tier (Common → Mythic)
export const fxUncoverTap = () => {
  tone(420, 0, 0.06, "sine", 0.09, 620);
  haptic("light");
};

export const fxUncoverCharge = (tier = "common") => {
  const base = { common: 220, rare: 260, epic: 300, legendary: 330, mythic: 360 }[tier] || 220;
  const dur = { common: 0.5, rare: 0.8, epic: 1.1, legendary: 1.5, mythic: 2.0 }[tier] || 0.5;
  tone(base, 0, dur, "sawtooth", 0.07, base * 4);
  haptic("light");
  if (tier === "legendary" || tier === "mythic") {
    setTimeout(() => haptic("medium"), dur * 500);
  }
};

export const fxUncoverReveal = (tier = "common") => {
  const melodies = {
    common: [523, 659],
    rare: [523, 659, 784],
    epic: [523, 659, 784, 1047],
    legendary: [392, 523, 659, 784, 1047, 1319],
    mythic: [261, 392, 523, 659, 784, 1047, 1319, 1568],
  };
  const notes = melodies[tier] || melodies.common;
  const step = tier === "mythic" ? 0.13 : tier === "legendary" ? 0.14 : 0.12;
  notes.forEach((f, i) => tone(f, i * step, 0.32, "triangle", 0.13));
  if (tier === "epic" || tier === "legendary" || tier === "mythic") {
    tone(notes[0] / 2, 0, 1.2, "sine", 0.1);
    tone(notes[notes.length - 1] * 2, notes.length * step, 0.6, "sine", 0.07);
  }
  if (tier === "mythic") {
    tone(65, 0, 1.8, "sawtooth", 0.09);
    setTimeout(() => haptic("success"), 120);
    setTimeout(() => haptic("success"), 480);
  }
  haptic("success");
};

// Gemma in rotazione 3D: whoosh + shimmer cristallino
export const fxGemSpin = () => {
  tone(196, 0, 0.5, "sine", 0.1, 392);
  [1319, 1568, 2093, 2637, 3136].forEach((f, i) => tone(f, 0.08 + i * 0.09, 0.22, "triangle", 0.08));
  tone(4186, 0.6, 0.55, "sine", 0.05);
  haptic("medium");
};

